import { FileExcelOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Descriptions,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { utils, writeFile } from 'xlsx';
import { usePagination } from '../shared/usePagination';
import { QP, useQueryParams } from '../shared/useQueryParams';
import {
  CancelExternalOrderResult,
  ExternalOrderItem,
  ExternalOrderItemListRow,
  useCancelExternalOrders,
  useExternalOrder,
  useExternalOrderLocations,
  useExternalOrderProviders,
  useExternalOrders,
} from '../utils/api-hooks/external-order.hook';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATE_COLOR: Record<string, string> = {
  已確認: 'green',
  未確認: 'orange',
  取消訂單: 'red',
  '取消訂單-退貨': 'red',
  結案: 'blue',
};

const CANCELLED = '取消訂單';

/** 依 mutation 回傳的 results 顯示成功/略過摘要。 */
function reportCancelResults(results: CancelExternalOrderResult[]) {
  const cancelled = results.filter(
    (r) => r.ok && r.reason !== 'already_cancelled',
  ).length;
  const already = results.filter((r) => r.reason === 'already_cancelled').length;
  const failed = results.filter((r) => !r.ok);
  message.success(`已取消 ${cancelled} 張訂單`);
  if (already > 0) message.info(`${already} 張原本已是取消狀態，略過`);
  if (failed.length > 0) {
    message.warning(
      `${failed.length} 張未取消：${failed
        .map((r) => `#${r.id}(${r.reason})`)
        .join('、')}`,
    );
  }
}

interface DetailModalProps {
  orderId: number | null;
  onClose: () => void;
}

function ExternalOrderDetailModal({ orderId, onClose }: DetailModalProps) {
  const { data, isLoading } = useExternalOrder(orderId);
  const cancelOrders = useCancelExternalOrders();

  const canCancel = data && data.order.state !== CANCELLED;

  const handleCancel = () => {
    if (!data) return;
    Modal.confirm({
      title: `取消訂單 ${data.order.serial}？`,
      content: '整張訂單會改為「取消訂單」並回補庫存，此操作無法在此復原。',
      okText: '確認取消訂單',
      okButtonProps: { danger: true },
      cancelText: '返回',
      onOk: async () => {
        const res = await cancelOrders.mutateAsync([data.order.id]);
        reportCancelResults(res.results);
      },
    });
  };

  return (
    <Modal
      open={orderId != null}
      onCancel={onClose}
      width={720}
      title="訂單明細"
      footer={
        canCancel ? (
          <Button
            danger
            loading={cancelOrders.isLoading}
            onClick={handleCancel}
          >
            取消訂單
          </Button>
        ) : null
      }
    >
      {isLoading || !data ? (
        <div style={{ textAlign: 'center', padding: 48 }}>載入中…</div>
      ) : (
        <>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="訂單編號" span={2}>
              {data.order.serial}
            </Descriptions.Item>
            <Descriptions.Item label="狀態">
              <Tag color={STATE_COLOR[data.order.state] ?? 'default'}>
                {data.order.state}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="取餐號">
              {data.order.pickup_no}
            </Descriptions.Item>
            <Descriptions.Item label="大樓" span={2}>
              {data.order.location_name}
            </Descriptions.Item>
            <Descriptions.Item label="收件人">
              {data.order.receiver_name}
            </Descriptions.Item>
            <Descriptions.Item label="會員">
              {data.order.user_name ?? data.order.user_email ?? '-'}
            </Descriptions.Item>
          </Descriptions>

          <Table<ExternalOrderItem>
            style={{ marginTop: 16 }}
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={data.items}
            columns={[
              {
                title: '品項',
                dataIndex: 'product_name',
              },
              {
                title: '供應商',
                dataIndex: 'provider_name',
              },
              {
                title: '數量',
                dataIndex: 'quantity',
                width: 80,
              },
              {
                title: '小計',
                key: 'subtotal',
                width: 100,
                render: (_, r) => r.price * r.quantity,
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
}

const externalOrdersQuerySchema = {
  keyword: QP.string('keyword'),
  productName: QP.string('productName'),
  providerId: QP.string('providerId'),
  range: QP.dayjsRange('startDate', 'endDate'),
  locationIds: QP.stringArray('locationIds'),
};

export function ExternalOrdersPage() {
  const pagination = usePagination({ defaultPageSize: 50, syncUrl: true });
  const { values, setValues } = useQueryParams(externalOrdersQuerySchema);
  const { keyword, productName, range } = values;
  const providerId = values.providerId ? Number(values.providerId) : undefined;
  const locationIds = useMemo(
    () => values.locationIds?.map(Number) ?? [],
    [values.locationIds],
  );

  const [detailId, setDetailId] = useState<number | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);

  const { data: locations } = useExternalOrderLocations();
  const { data: providers } = useExternalOrderProviders();
  const cancelOrders = useCancelExternalOrders();

  const queryParams = useMemo(
    () => ({
      skip: pagination.skip,
      take: pagination.take,
      keyword: keyword || undefined,
      startDate: range.startTime?.startOf('day'),
      endDate: range.endTime?.endOf('day'),
      locationIds: locationIds.length > 0 ? locationIds : undefined,
      productName: productName || undefined,
      providerId,
    }),
    [
      pagination.skip,
      pagination.take,
      keyword,
      range,
      locationIds,
      productName,
      providerId,
    ],
  );

  const { data, isFetching } = useExternalOrders(queryParams);

  const rows = useMemo(() => data?.items ?? [], [data]);

  // 列表以「品項」為列，取消是整張訂單 —— 把勾選的品項列去重回訂單。
  const selectedOrders = useMemo(() => {
    const picked = rows.filter((r) => selectedKeys.includes(r.item_id));
    const byOrder = new Map<number, ExternalOrderItemListRow>();
    for (const r of picked)
      if (!byOrder.has(r.order_id)) byOrder.set(r.order_id, r);
    return Array.from(byOrder.values());
  }, [rows, selectedKeys]);

  const handleBatchCancel = () => {
    if (selectedOrders.length === 0) return;
    Modal.confirm({
      title: `取消 ${selectedOrders.length} 張訂單？`,
      width: 560,
      content: (
        <div style={{ maxHeight: 320, overflow: 'auto' }}>
          <Text type="secondary">
            勾選的品項會以「整張訂單」為單位取消（已去重），並回補庫存，無法在此復原。
          </Text>
          <ul style={{ paddingLeft: 18, marginTop: 8 }}>
            {selectedOrders.map((o) => (
              <li key={o.order_id}>
                {o.serial}｜取餐號 {o.pickup_no}｜
                {dayjs(o.redeem_time).format('YYYY-MM-DD')}｜
                {o.user_name ?? o.user_email ?? '-'}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: '確認取消',
      okButtonProps: { danger: true },
      cancelText: '返回',
      onOk: async () => {
        const res = await cancelOrders.mutateAsync(
          selectedOrders.map((o) => o.order_id),
        );
        reportCancelResults(res.results);
        setSelectedKeys([]);
      },
    });
  };

  const handleExport = async () => {
    const hasFilter =
      range.startTime ||
      range.endTime ||
      locationIds.length > 0 ||
      providerId ||
      productName ||
      keyword;
    if (!hasFilter) {
      const ok = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '未設定任何篩選條件',
          content: '將匯出全部外部訂單，資料量可能很大，確定繼續？',
          okText: '繼續匯出',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }
    const { data: all } = await axios.get<{ items: ExternalOrderItemListRow[] }>(
      '/external-orders',
      {
        params: {
          skip: 0,
          take: 100000,
          keyword: keyword || undefined,
          startDate: range.startTime?.startOf('day').toISOString(),
          endDate: range.endTime?.endOf('day').toISOString(),
          locationIds: locationIds.length > 0 ? locationIds : undefined,
          productName: productName || undefined,
          providerId,
        },
      },
    );
    const sheet = utils.json_to_sheet(
      all.items.map((r) => ({
        訂單編號: r.serial,
        取餐號: r.pickup_no,
        取餐時間: dayjs(r.redeem_time).format('YYYY-MM-DD'),
        大樓: r.location_name,
        收件人: r.receiver_name,
        會員: r.user_name ?? r.user_email ?? '',
        餐廳: r.provider_name,
        品項: r.product_name,
        數量: r.quantity,
        小計: r.price * r.quantity,
        狀態: r.state,
        建立時間: dayjs(r.time).format('YYYY-MM-DD HH:mm'),
      })),
    );
    const wb = utils.book_new();
    utils.book_append_sheet(wb, sheet, '外部訂單');
    writeFile(wb, `外部訂單-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`);
  };

  const columns: ColumnsType<ExternalOrderItemListRow> = [
    {
      title: '訂單編號',
      dataIndex: 'serial',
      width: 150,
      render: (serial: string, row) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => setDetailId(row.order_id)}
        >
          {serial}
        </Button>
      ),
    },
    { title: '取餐號', dataIndex: 'pickup_no', width: 90 },
    {
      title: '取餐時間',
      dataIndex: 'redeem_time',
      width: 120,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD'),
    },
    { title: '大樓', dataIndex: 'location_name', width: 120 },
    { title: '收件人', dataIndex: 'receiver_name', width: 100 },
    {
      title: '會員',
      dataIndex: 'user_name',
      width: 120,
      render: (v: string | null, row) => v ?? row.user_email ?? '-',
    },
    { title: '餐廳', dataIndex: 'provider_name', width: 130 },
    { title: '品項', dataIndex: 'product_name' },
    { title: '數量', dataIndex: 'quantity', width: 70 },
    {
      title: '小計',
      key: 'subtotal',
      width: 90,
      render: (_, r) => r.price * r.quantity,
    },
    {
      title: '狀態',
      dataIndex: 'state',
      width: 110,
      render: (state: string) => (
        <Tag color={STATE_COLOR[state] ?? 'default'}>{state}</Tag>
      ),
    },
    {
      title: '建立時間',
      dataIndex: 'time',
      width: 160,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
  ];

  return (
    <div>
      <Title level={3}>外部訂單</Title>

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          value={[range.startTime ?? null, range.endTime ?? null] as any}
          onChange={(v: any) =>
            setValues({
              range: {
                startTime: v?.[0] ?? undefined,
                endTime: v?.[1] ?? undefined,
              },
            })
          }
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="大樓"
          style={{ minWidth: 160 }}
          value={locationIds}
          onChange={(v: number[]) => setValues({ locationIds: v.map(String) })}
          options={(locations ?? []).map((l) => ({
            label: l.location_name,
            value: l.location_id,
          }))}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="供應商"
          style={{ minWidth: 180 }}
          value={providerId}
          onChange={(v?: number) =>
            setValues({ providerId: v ? String(v) : '' })
          }
          options={(providers ?? []).map((p) => ({
            label: p.provider_name,
            value: p.provider_id,
          }))}
        />
        <Input.Search
          key={`product-${productName}`}
          allowClear
          placeholder="品項名稱"
          style={{ width: 140 }}
          defaultValue={productName}
          onSearch={(v) => setValues({ productName: v })}
        />
        <Input.Search
          key={`keyword-${keyword}`}
          allowClear
          placeholder="訂單編號/取餐號/收件人/大樓"
          style={{ width: 240 }}
          defaultValue={keyword}
          onSearch={(v) => setValues({ keyword: v })}
        />
        <Button icon={<FileExcelOutlined />} onClick={handleExport}>
          匯出 Excel
        </Button>
        {selectedOrders.length > 0 && (
          <Button
            danger
            loading={cancelOrders.isLoading}
            onClick={handleBatchCancel}
          >
            取消所選訂單（{selectedOrders.length}）
          </Button>
        )}
      </Space>

      <Table<ExternalOrderItemListRow>
        size="small"
        rowKey="item_id"
        loading={isFetching}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          getCheckboxProps: (row) => ({ disabled: row.state === CANCELLED }),
        }}
        pagination={{ ...pagination, total: data?.total ?? 0 }}
      />

      <ExternalOrderDetailModal
        orderId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}
